import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode'; // Import corrigido: agora é um named import
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

// ✅ Criação do Contexto de Autenticação
export const AuthContext = createContext();

// ✅ Provedor de Autenticação
const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [auth, setAuth] = useState({
        token: localStorage.getItem('token') || null,
        roleId: localStorage.getItem('role_id') || null,
        user: null,
    });
    const [sessionExpired, setSessionExpired] = useState(false);

    // ✅ Busca dados do usuário (incluindo permissões)
    const fetchUser = async (token) => {
        try {
            const response = await fetch(`${API_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const userData = await response.json();
                setAuth(prev => ({ ...prev, user: userData }));
            } else if (response.status === 401) {
                logout(true); // Expired session
            }
        } catch (err) {
            console.error("Erro ao buscar dados do usuário:", err);
        }
    };

    // ✅ Verifica o token no carregamento do componente
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);

                // Verifica se o token está expirado
                if (decoded.exp * 1000 < Date.now()) {
                    console.warn('🔴 Token expirado. Fazendo logout...');
                    logout();
                    return;
                }

                // Atualiza o estado inicial (parcial)
                setAuth({
                    token,
                    roleId: decoded.role_id,
                    user: { email: decoded.sub }, // Temporário até fetchUser
                });

                // Busca dados completos
                fetchUser(token);

            } catch (err) {
                console.error('🔴 Erro ao decodificar token:', err);
                logout();
            }
        }
    }, []);

    // ✅ Listener para evento global de Sessão Expirada (disparado pelo api.js)
    useEffect(() => {
        const handleSessionExpired = () => {
            console.warn("⚠️ Evento 'session-expired' recebido.");
            logout(true);
        };

        window.addEventListener('session-expired', handleSessionExpired);
        return () => window.removeEventListener('session-expired', handleSessionExpired);
    }, []);

    // ✅ Timer de Inatividade (Idle Timer)
    useEffect(() => {
        let idleTimer;
        const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutos

        const resetTimer = () => {
            if (idleTimer) clearTimeout(idleTimer);
            if (auth.token) {
                idleTimer = setTimeout(() => {
                    console.warn("⏲️ Tempo de inatividade atingido.");
                    logout(true);
                }, IDLE_TIMEOUT);
            }
        };

        // Eventos que indicam atividade
        const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
        
        if (auth.token) {
            activityEvents.forEach(event => window.addEventListener(event, resetTimer));
            resetTimer(); // Inicia o timer
        }

        return () => {
            if (idleTimer) clearTimeout(idleTimer);
            activityEvents.forEach(event => window.removeEventListener(event, resetTimer));
        };
    }, [auth.token]);

    // ... (useEffect unauthorized handler) ...

    // ✅ Realiza o login e configura o estado
    const login = async (token, forceChangePassword = false) => {
        try {
            const decoded = jwtDecode(token);

            // Se forçar troca de senha, não salva token e não redireciona ainda
            // Na verdade, precisamos do token para chamar a API de troca de senha.
            // Vamos salvar, mas retornar a flag para o componente decidir o redirect.

            // Salva o token e role no localStorage
            localStorage.setItem('token', token);
            localStorage.setItem('role_id', decoded.role_id);

            // Estado inicial
            setAuth({
                token,
                roleId: decoded.role_id,
                user: { email: decoded.sub },
            });

            // Se não for troca de senha obrigatória, busca dados completos
            if (!forceChangePassword) {
                await fetchUser(token);
                console.info('🟢 Login bem-sucedido. Redirecionando...');

                // Todos vão para a Home, e o MainMenu decide o que mostrar
                navigate('/home');
            } else {
                console.info('🟡 Troca de senha obrigatória. Aguardando ação do usuário.');
            }

            return { success: true };

        } catch (err) {
            console.error('🔴 Erro ao decodificar token durante o login:', err);
            logout();
            return { success: false, error: err.message };
        }
    };

    // ✅ Realiza o logout e reseta o estado
    const logout = (isExpired = false) => {
        localStorage.removeItem('token');
        localStorage.removeItem('role_id');
        setAuth({ token: null, roleId: null, user: null });
        
        if (isExpired) {
            setSessionExpired(true);
        } else {
            console.info('🟡 Logout bem-sucedido. Redirecionando para página inicial.');
            navigate('/login'); // Redireciona para login explicitamente
        }
    };

    // ✅ Verifica se o usuário possui uma role específica
    const hasRole = (role) => auth.roleId === role;

    // ✅ Verifica se o usuário está autenticado
    const isAuthenticated = () => auth.token !== null;

    // ✅ Define as rotas disponíveis
    const routes = {
        public: [
            '/health/health',
            '/',
        ],
        authenticated: [
            '/chat/chat/',
            '/chat/chat/ping',
            '/profile/profile',
        ],
        admin: [
            '/bi/info', // ✅ Adicionada a rota para BI
            '/auth/roles',
            '/auth/users',
            '/admin/users',
            '/admin/roles',
            '/admin/assign-role',
            '/admin/users/{user_id}/password',
            '/admin/users/{user_id}',
        ],
        auth: [
            '/auth/login',
            '/auth/register',
        ],
    };

    return (
        <AuthContext.Provider value={{
            auth,
            login,
            logout,
            hasRole,
            isAuthenticated,
            routes,
            sessionExpired,
            setSessionExpired
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// ✅ Exportação Padrão
export default AuthProvider;
